import type { RTCSession } from 'jssip/lib/RTCSession';

import { selectNoiseSuppressionEngine } from 'flow/selectors/headset-selectors';
import { AppDispatch } from 'flow/types/Redux';
import { RTCSession as FlowRTCSession } from 'flow/types/RTCSession';
import { State } from 'flow/types/State';
import { addPhoneLogEntry } from 'flow/utils/browser-utils';
import {
    createNoiseSuppressedTrack,
    isNoiseSuppressionSupported,
} from 'flow/utils/noise-suppression-utils';
import { rtcSessionRepresentsTicketNonVoice } from 'flow/utils/ticket-utils';

/**
 * The session as the ticket helper types it, plus the JsSIP members this file
 * reads. JsSIP's typings leave out the `_request` our type declares, and its
 * `direction` is an enum the two types cannot share, so this starts from ours.
 */
export type SoftphoneSession = FlowRTCSession &
    Pick<RTCSession, 'connection' | 'data' | 'isEnded'>;

/**
 * A thrown value is `unknown`, and an object in a template literal prints as
 * `[object Object]`. The audio APIs reject with an Error or a DOMException, so
 * name and message carry what the log needs.
 */
const describeError = (error: unknown): string => {
    if (error instanceof Error) {
        return `${error.name}: ${error.message}`;
    }

    return typeof error === 'string' ? error : 'unknown error';
};

/**
 * Swap the microphone we are sending for a denoised copy of it.
 *
 * Deliberately additive: the call is already up and sending raw microphone by
 * the time this runs, and we only replace the sender's track once the model is
 * loaded. If anything here fails the call simply carries on undenoised, which is
 * the right failure mode for audio the customer is already talking over.
 *
 * The graph reads from the track the sender held at `confirmed`. No softphone
 * path changes the microphone mid-call today. One that does must release this
 * graph and build it again on the new track, or it swaps the raw track back in
 * and leaves the graph running.
 */
export const applyNoiseSuppression =
    (session?: SoftphoneSession) =>
    async (_dispatch: AppDispatch, getState: () => State) => {
        if (!session) {
            return;
        }

        const engine = selectNoiseSuppressionEngine(getState());

        if (
            engine === 'off' ||
            !isNoiseSuppressionSupported() ||
            rtcSessionRepresentsTicketNonVoice(session)
        ) {
            return;
        }

        const sender = session.connection
            ?.getSenders()
            .find((candidate) => candidate.track?.kind === 'audio');

        if (!sender?.track) {
            addPhoneLogEntry(
                'Noise suppression: no audio sender to process, leaving call untouched',
            );
            return;
        }

        const sourceTrack = sender.track;

        try {
            const suppressed = await createNoiseSuppressedTrack(
                sourceTrack,
                engine,
            );

            if (session.isEnded()) {
                await suppressed.release();
                return;
            }

            /* Register the teardown before the swap. A call that ends while
               replaceTrack is pending is torn down by releaseNoiseSuppression,
               which must find the graph or it leaks for the life of the page. */
            session.data.releaseNoiseSuppression = suppressed.release;

            /* Mute and hold are `track.enabled` on whatever the sender holds
               (JsSIP _toggleMuteAudio), so the new track must arrive in the
               same state as the one it replaces. */
            suppressed.track.enabled = sourceTrack.enabled;

            await sender.replaceTrack(suppressed.track);

            if (session.isEnded()) {
                /* releaseNoiseSuppression tore the graph down mid-swap. */
                return;
            }

            /* The sender held the raw track until the swap resolved, so a mute
               pressed in the meantime landed there. Carry the final state over,
               then re-enable the raw track: it now only feeds the graph, and a
               disabled source feeds silence that no unmute can lift. */
            suppressed.track.enabled = sourceTrack.enabled;
            sourceTrack.enabled = true;

            addPhoneLogEntry(
                `Noise suppression active (${engine}) for call ${session.data['callId']}`,
            );
        } catch (error: unknown) {
            addPhoneLogEntry(
                `Noise suppression (${engine}) failed, continuing with the raw microphone: ${describeError(error)}`,
            );
        }
    };

/**
 * Tear down the graph a call was sending through, if it had one. JsSIP stops
 * only the stream it opened itself, and an AudioContext left running keeps the
 * machine awake.
 */
export const releaseNoiseSuppression = (session: SoftphoneSession) => {
    const release: (() => Promise<void>) | undefined =
        session.data.releaseNoiseSuppression;

    if (!release) {
        return;
    }

    session.data.releaseNoiseSuppression = undefined;
    release().catch((error: unknown) => {
        addPhoneLogEntry(
            `Noise suppression failed to release cleanly: ${describeError(error)}`,
        );
    });
};
